import { Box, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import AdminResourceCrudPage from './components/AdminResourceCrudPage';

const usageBar = (used, total, label) => {
  const pct = total ? Math.min(100, Math.round((Number(used || 0) / Number(total || 1)) * 100)) : 0;
  return (
    <Box sx={{ minWidth: 180 }}>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {used || 0}/{total || 0}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          mt: 0.5,
          height: 6,
          borderRadius: 99,
          bgcolor: 'rgba(15, 23, 42, 0.08)',
          '& .MuiLinearProgress-bar': { bgcolor: pct > 90 ? '#ef4444' : pct > 70 ? '#f59e0b' : '#22c55e' }
        }}
      />
    </Box>
  );
};

const AdminQuotasPage = () => {
  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'scope', label: 'Scope' },
    { key: 'organization_name', label: 'Organization' },
    { key: 'user_email', label: 'User' },
    {
      key: 'limits',
      label: 'Limits',
      render: (row) =>
        `${row.max_targets || 0} targets • ${row.max_scans_daily || 0}/day • ${row.max_concurrent_scans || 0} concurrent`
    },
    {
      key: 'monthly',
      label: 'Monthly Capacity',
      render: (row) => (
        <Stack spacing={0.8}>
          {usageBar(row.used_scan_tokens_monthly, row.max_scan_tokens_monthly, 'tokens')}
          {usageBar(row.used_hosts_monthly, row.max_hosts_monthly, 'hosts')}
        </Stack>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (row) =>
        row.is_active ? (
          <Chip size="small" label="Active" sx={{ bgcolor: 'rgba(22,163,74,0.12)', color: '#166534', fontWeight: 600 }} />
        ) : (
          <Chip size="small" label="Inactive" sx={{ bgcolor: 'rgba(220,38,38,0.12)', color: '#991b1b', fontWeight: 600 }} />
        )
    }
  ];

  const fields = [
    { name: 'name', label: 'Name', type: 'text', grid: 6 },
    {
      name: 'scope',
      label: 'Scope',
      type: 'select',
      grid: 6,
      defaultValue: 'global',
      options: [
        { value: 'global', label: 'Global' },
        { value: 'organization', label: 'Organization' },
        { value: 'user', label: 'User' }
      ]
    },
    { name: 'organization', label: 'Organization ID (optional)', type: 'number', grid: 6, nullable: true },
    { name: 'user', label: 'User ID (optional)', type: 'number', grid: 6, nullable: true },
    { name: 'max_targets', label: 'Max Targets', type: 'number', grid: 3 },
    { name: 'max_scans_daily', label: 'Max Scans/Day', type: 'number', grid: 3 },
    { name: 'max_concurrent_scans', label: 'Max Concurrent', type: 'number', grid: 3 },
    { name: 'max_upload_mb', label: 'Max Upload MB', type: 'number', grid: 3 },
    { name: 'max_scan_tokens_monthly', label: 'Monthly Scan Tokens', type: 'number', grid: 3, defaultValue: 1000 },
    { name: 'max_hosts_monthly', label: 'Monthly Hosts', type: 'number', grid: 3, defaultValue: 500 },
    { name: 'is_active', label: 'Active', type: 'switch', grid: 6, defaultValue: true }
  ];

  return (
    <AdminResourceCrudPage
      title="Quotas & Limits"
      subtitle="Control scan volume, concurrency, and upload size by scope."
      icon={SpeedOutlinedIcon}
      endpoint="/api/admin/quotas/"
      columns={columns}
      fields={fields}
    />
  );
};

export default AdminQuotasPage;
